import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Globe, ArrowLeft, ExternalLink, MapPin, Calendar, FileText, Image as ImgIcon, Lock, AlertCircle, Loader2, Building2, Package,
  CheckCircle2, XCircle, MessageSquare, ThumbsUp, ThumbsDown, Download, ChevronLeft, ChevronRight, X as XIcon, Pencil, Trash2,
} from "lucide-react";
import DnfLogo from "@/components/DnfLogo";
import ProyectoThumb from "@/components/ProyectoThumb";
import PhotoLightbox from "@/components/PhotoLightbox";
import { nombreFotoBase } from "@/lib/fotoUtils";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { EstadisticasView } from "@/pages/Estadisticas";
import { PortalConfiguracion } from "@/pages/PortalCliente.Config";
import { PortalSolicitudForm } from "@/pages/PortalCliente.Solicitud";
import { PortalFacturas, PortalResumenCards, PortalActividad, PortalEspacioGaleria } from "@/pages/PortalCliente.Extras";
import { portalApi, setPortalToken, getPortalToken, PORTAL_TOKEN_KEY } from "@/lib/portalApi";

const api = portalApi;

const ESTADO_PETICION = {
  solicitado_cliente: { label: "Solicitado por ti", color: "bg-violet-500/30 text-violet-200" },
  borrador: { label: "En preparación", color: "bg-zinc-700/40 text-zinc-300" },
  presupuesto_aprobado: { label: "Presupuesto aprobado", color: "bg-purple-500/30 text-purple-200" },
  presupuesto_enviado: { label: "Presupuesto enviado", color: "bg-blue-500/30 text-blue-200" },
  aprobada: { label: "Pendiente", color: "bg-amber-500/30 text-amber-100" },
  pendiente: { label: "Pendiente", color: "bg-amber-500/30 text-amber-100" },
  en_produccion: { label: "En producción", color: "bg-orange-500/30 text-orange-200" },
  en_ruta: { label: "En ruta", color: "bg-cyan-500/30 text-cyan-200" },
  montado_parcial: { label: "Montaje parcial", color: "bg-yellow-500/30 text-yellow-100" },
  entregada: { label: "Montado", color: "bg-emerald-500/30 text-emerald-200" },
  cerrada: { label: "Montado", color: "bg-emerald-500/30 text-emerald-200" },
  facturada: { label: "Facturada", color: "bg-teal-500/30 text-teal-200" },
  cancelada: { label: "Cancelada", color: "bg-red-500/30 text-red-200" },
  incidencia: { label: "Incidencia", color: "bg-red-500/30 text-red-100 border border-red-500/50" },
  // Legacy
  planificada: { label: "Planificada", color: "bg-blue-500/30 text-blue-200" },
  finalizada: { label: "Montado", color: "bg-emerald-500/30 text-emerald-200" },
};

const TIPO_LINEA = {
  montaje: "Montaje",
  produccion_montaje: "Producción + Montaje",
  solo_montaje: "Solo montaje",
  retirada: "Retirada",
  incidencia: "Incidencia",
  "2a_visita": "2ª Visita",
};

// Estado del montaje visible para el cliente
const ESTADO_REAL = {
  montado:     { label: "Montado", className: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
  incidencia:  { label: "Incidencia", className: "bg-red-500/20 text-red-300 border-red-500/40" },
  programada:  { label: "Programada", className: "bg-amber-500/20 text-amber-300 border-amber-500/40" },
  pendiente:   { label: "Pendiente", className: "bg-zinc-700/40 text-zinc-300 border-zinc-700" },
};

function PortalShell({ cliente, children, onBack, title }) {
  const clienteLogo = cliente?.logo_url;
  const [logoFailed, setLogoFailed] = React.useState(false);
  const iniciales = (cliente?.nombre || "")
    .split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
  return (
    <div className="min-h-screen bg-[#0a0a0c] text-zinc-100" translate="no" lang="es">
      <header className="sticky top-0 z-30 bg-[#0c0c10] border-b border-zinc-800 px-4 py-3 flex items-center gap-3" translate="no">
        {onBack ? (
          <button onClick={onBack} data-testid="portal-back" className="p-1.5 rounded hover:bg-zinc-900">
            <ArrowLeft className="w-5 h-5" />
          </button>
        ) : (
          <DnfLogo size={36} />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-amber-400 truncate">
            Portal RotulaciónDNF
          </div>
          <div className="text-sm font-semibold truncate">
            {title || cliente?.nombre || "Portal cliente"}
          </div>
        </div>
        {clienteLogo && !logoFailed ? (
          <img src={clienteLogo} alt={cliente?.nombre || "Logo cliente"}
            data-testid="portal-cliente-logo"
            onError={() => setLogoFailed(true)}
            className="h-9 max-w-[110px] object-contain rounded bg-white/90 px-1.5 py-0.5" />
        ) : cliente ? (
          <div data-testid="portal-cliente-iniciales"
            className="h-9 w-9 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 text-xs font-bold flex items-center justify-center shrink-0"
            title={cliente?.nombre || ""}>
            {iniciales}
          </div>
        ) : null}
        <a href="https://www.rotulaciondnf.com" target="_blank" rel="noreferrer"
          className="text-[11px] text-zinc-500 hover:text-amber-300 flex items-center gap-1">
          <ExternalLink className="w-3 h-3" /> rotulaciondnf.com
        </a>
      </header>
      <main className="max-w-5xl mx-auto">{children}</main>
      <footer className="text-center text-[11px] text-zinc-600 py-6 border-t border-zinc-900 mt-12">
        <DnfLogo size={28} className="mx-auto opacity-50 mb-2" />
        RotulaciónDNF · Producción gráfica · 624 49 12 71
      </footer>
    </div>
  );
}

export default function PortalCliente() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pid: pidFromUrl } = useParams(); // soporte /portal/p/:pid (futuro)
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState("lista"); // lista | detalle
  const [pid, setPid] = useState(null);

  // Si llega ?token=... lo guardamos y limpiamos URL.
  // Ejecutar solo al montar (intencional): leer query string una vez.
  useEffect(() => {
    const t = searchParams.get("token");
    if (t) {
      setPortalToken(t);
      const next = new URLSearchParams(searchParams);
      next.delete("token");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMe = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api().get("/portal/me");
      setMe(data);
      // Si scope=peticion, vamos directo al detalle
      if (data.scope === "peticion" && data.peticion_id) {
        setPid(data.peticion_id);
        setView("detalle");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Acceso no válido");
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar /me solo cuando hay token. searchParams se ignora intencionalmente
  // porque el primer useEffect ya lo consume al montar.
  useEffect(() => {
    const t = getPortalToken();
    if (t) { loadMe(); }
    else { setLoading(false); }
  }, [loadMe]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center text-zinc-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando...
      </div>
    );
  }

  if (error || !me) {
    return (
      <PortalShell cliente={null}>
        <div className="p-8 max-w-md mx-auto text-center">
          <Lock className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold mb-2">Acceso necesario</h2>
          <p className="text-sm text-zinc-400 mb-4">
            {error || "Necesitas un enlace de acceso. Si tu comercial en DNF te lo envió, ábrelo directamente."}
          </p>
          <ManualTokenForm onSubmit={loadMe} />
        </div>
      </PortalShell>
    );
  }

  if (view === "detalle" && pid) {
    return <PortalPeticionDetalle pid={pid} cliente={me.cliente} config={me.config} scope={me.scope}
      onBack={me.scope === "cliente" ? () => { setView("lista"); setPid(null); } : null} />;
  }

  return <PortalListaPeticiones cliente={me.cliente} config={me.config}
    onOpenPeticion={(id) => { setPid(id); setView("detalle"); }} />;
}

function ManualTokenForm({ onSubmit }) {
  const [token, setToken] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); setPortalToken(token.trim()); onSubmit(); }}>
      <Input value={token} onChange={(e) => setToken(e.target.value)}
        placeholder="Pega aquí tu token de acceso"
        data-testid="portal-manual-token"
        className="bg-zinc-900 border-zinc-800 text-xs mb-2" />
      <Button type="submit" data-testid="portal-manual-submit"
        className="w-full bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
        Acceder
      </Button>
    </form>
  );
}

function PortalListaPeticiones({ cliente, config, onOpenPeticion }) {
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("campanas"); // campanas | estadisticas | configuracion
  const [clienteState, setClienteState] = useState(cliente);
  // Iter 82 — Feedback Floren: filtros + ordenar + persistencia en sessionStorage
  const FS_KEY = `dnf-portal-filtros-${cliente?.id || "x"}-v1`;
  const _fs = (() => { try { return JSON.parse(sessionStorage.getItem(FS_KEY) || "{}"); } catch { return {}; } })();
  const [q, setQ] = useState(_fs.q || "");
  const [filtroEstado, setFiltroEstado] = useState(_fs.filtroEstado || "activas");
  const [filtroFecha, setFiltroFecha] = useState(_fs.filtroFecha || "todo");
  const [orden, setOrden] = useState(_fs.orden || "fecha_desc");
  useEffect(() => {
    try { sessionStorage.setItem(FS_KEY, JSON.stringify({ q, filtroEstado, filtroFecha, orden })); }
    catch { /* private mode */ }
  }, [q, filtroEstado, filtroFecha, orden, FS_KEY]);

  useEffect(() => {
    api().get("/portal/peticiones")
      .then(({ data }) => setPets(data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Error cargando peticiones"))
      .finally(() => setLoading(false));
  }, []);

  // Filtro + ordenación derivados (memo barato — pocas peticiones)
  const ESTADOS_CERRADOS = new Set(["finalizada", "finalizada_manual", "cancelada_manual", "facturada", "entregada", "cancelada"]);
  const petsFiltrados = pets
    .filter((p) => {
      // texto
      if (q.trim()) {
        const t = q.toLowerCase();
        const hay = [p.codigo, p.nombre, p.marca, p.descripcion].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(t)) return false;
      }
      // estado
      const cerrada = ESTADOS_CERRADOS.has(p.estado);
      if (filtroEstado === "activas" && cerrada) return false;
      if (filtroEstado === "completadas" && !cerrada) return false;
      if (filtroEstado === "canceladas" && !["cancelada", "cancelada_manual"].includes(p.estado)) return false;
      // fecha
      if (filtroFecha !== "todo") {
        const fecha = p.fecha_objetivo || p.created_at;
        if (!fecha) return false;
        const d = new Date(fecha);
        const now = new Date();
        const diff = (now - d) / (1000 * 60 * 60 * 24);
        if (filtroFecha === "mes" && diff > 31) return false;
        if (filtroFecha === "3meses" && diff > 92) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (orden === "fecha_desc") return (b.fecha_objetivo || b.created_at || "").localeCompare(a.fecha_objetivo || a.created_at || "");
      if (orden === "fecha_asc") return (a.fecha_objetivo || a.created_at || "").localeCompare(b.fecha_objetivo || b.created_at || "");
      if (orden === "estado") return (a.estado || "").localeCompare(b.estado || "");
      return 0;
    });

  return (
    <PortalShell cliente={clienteState}>
      <div className="p-4 sm:p-6">
        <div className="mb-4 flex items-end justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">
              {tab === "campanas" ? "Tus campañas"
                : tab === "solicitar" ? "Solicitar trabajo"
                : tab === "facturas" ? "Facturación"
                : tab === "estadisticas" ? "Estadísticas" : "Configuración"}
            </h1>
            <p className="text-xs text-zinc-500 mt-1">
              {tab === "campanas" ? "Listado de proyectos activos y completados"
                : tab === "solicitar" ? "Pide un presupuesto o lanza un trabajo en firme"
                : tab === "facturas" ? "Tus facturas: pagadas, pendientes y vencimientos"
                : tab === "estadisticas" ? "Análisis de tus trabajos · cadenas · provincias · mapa"
                : "Perfil, notificaciones, contactos y sub-portales"}
            </p>
          </div>
          <div className="flex gap-1 bg-zinc-900/60 border border-zinc-800 rounded-lg p-1 flex-wrap">
            <button onClick={() => setTab("campanas")}
              data-testid="portal-tab-campanas"
              className={`px-3 py-1.5 text-xs rounded ${tab === "campanas" ? "bg-amber-500 text-zinc-900 font-semibold" : "text-zinc-400 hover:text-zinc-200"}`}>
              Campañas
            </button>
            <button onClick={() => setTab("solicitar")}
              data-testid="portal-tab-solicitar"
              className={`px-3 py-1.5 text-xs rounded ${tab === "solicitar" ? "bg-amber-500 text-zinc-900 font-semibold" : "text-zinc-400 hover:text-zinc-200"}`}>
              + Solicitar
            </button>
            {config?.ver_facturas !== false && (
              <button onClick={() => setTab("facturas")}
                data-testid="portal-tab-facturas"
                className={`px-3 py-1.5 text-xs rounded ${tab === "facturas" ? "bg-amber-500 text-zinc-900 font-semibold" : "text-zinc-400 hover:text-zinc-200"}`}>
                Facturas
              </button>
            )}
            <button onClick={() => setTab("estadisticas")}
              data-testid="portal-tab-stats"
              className={`px-3 py-1.5 text-xs rounded ${tab === "estadisticas" ? "bg-amber-500 text-zinc-900 font-semibold" : "text-zinc-400 hover:text-zinc-200"}`}>
              Estadísticas
            </button>
            <button onClick={() => setTab("configuracion")}
              data-testid="portal-tab-config"
              className={`px-3 py-1.5 text-xs rounded ${tab === "configuracion" ? "bg-amber-500 text-zinc-900 font-semibold" : "text-zinc-400 hover:text-zinc-200"}`}>
              ⚙️ Configuración
            </button>
          </div>
        </div>

        {tab === "configuracion" ? (
          <PortalConfiguracion cliente={clienteState} onClienteUpdated={(c) => setClienteState({ ...clienteState, ...c })} />
        ) : tab === "solicitar" ? (
          <PortalSolicitudForm onCreated={() => {
            api().get("/portal/peticiones").then(({ data }) => setPets(data)).catch(() => {});
          }} />
        ) : tab === "facturas" ? (
          <PortalFacturas />
        ) : tab === "estadisticas" ? (
          <>
            <PortalResumenCards />
            <PortalEstadisticasView />
          </>
        ) : (
          <>
            <PortalActividad />
            {loading && (
              <div className="text-center py-8 text-zinc-500">
                <Loader2 className="w-5 h-5 animate-spin inline" />
              </div>
            )}
            {!loading && pets.length === 0 && (
              <div className="text-center py-12 text-zinc-500">
                <Building2 className="w-10 h-10 mx-auto opacity-40 mb-2" />
                <div className="text-sm">Aún no hay campañas registradas.</div>
              </div>
            )}
            {!loading && pets.length > 0 && (
              <div className="mb-3 bg-zinc-900/40 border border-zinc-800 rounded-lg p-2.5 space-y-2"
                   data-testid="portal-filtros">
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Buscar (código, nombre, marca…)"
                  data-testid="portal-search"
                  className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm" />
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}
                    data-testid="portal-filter-estado"
                    className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5">
                    <option value="activas">Activas</option>
                    <option value="completadas">Completadas</option>
                    <option value="canceladas">Canceladas</option>
                    <option value="todas">Todas</option>
                  </select>
                  <select value={filtroFecha} onChange={(e) => setFiltroFecha(e.target.value)}
                    data-testid="portal-filter-fecha"
                    className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5">
                    <option value="todo">Todo el tiempo</option>
                    <option value="mes">Último mes</option>
                    <option value="3meses">Últimos 3 meses</option>
                  </select>
                  <select value={orden} onChange={(e) => setOrden(e.target.value)}
                    data-testid="portal-order"
                    className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5">
                    <option value="fecha_desc">Más reciente</option>
                    <option value="fecha_asc">Más antigua</option>
                    <option value="estado">Por estado</option>
                  </select>
                </div>
                <div className="text-[10px] text-zinc-500 flex items-center justify-between">
                  <span>Mostrando <b className="text-zinc-300">{petsFiltrados.length}</b> de {pets.length}</span>
                  {(q || filtroEstado !== "activas" || filtroFecha !== "todo" || orden !== "fecha_desc") && (
                    <button onClick={() => { setQ(""); setFiltroEstado("activas"); setFiltroFecha("todo"); setOrden("fecha_desc"); }}
                      data-testid="portal-clear-filtros"
                      className="text-amber-400 hover:underline">Limpiar filtros</button>
                  )}
                </div>
              </div>
            )}
            <div className="space-y-2" data-testid="portal-peticiones-list">
              {petsFiltrados.map((p) => {
                const est = ESTADO_PETICION[p.estado] || ESTADO_PETICION.borrador;
                return (
                  <button key={p.id} onClick={() => onOpenPeticion(p.id)}
                    data-testid={`portal-peticion-${p.codigo}`}
                    className="w-full text-left bg-zinc-900/60 border border-zinc-800 hover:border-amber-500/50 rounded-xl p-3 transition-colors">
                    <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                      <div className="flex items-center gap-2">
                        <ProyectoThumb url={p.imagen_url} nombre={p.nombre} size={34} rounded="lg"
                          data-testid={`portal-peticion-${p.codigo}-thumb`} />
                        {p.cadena_logo_url && (
                          <img src={p.cadena_logo_url} alt={p.cadena_nombre || "cadena"}
                            title={p.cadena_nombre || ""}
                            data-testid={`portal-peticion-${p.codigo}-cadena-logo`}
                            className="h-6 max-w-[80px] object-contain bg-white/95 rounded px-1 py-0.5" />
                        )}
                        <span className="text-[10px] text-amber-400 font-mono">{p.codigo}</span>
                      </div>
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${est.color}`}>{est.label}</span>
                    </div>
                    <div className="text-sm font-semibold text-zinc-100">{p.nombre}</div>
                    {p.marca && <div className="text-xs text-violet-300 mt-0.5">{p.marca}</div>}
                    {p.descripcion && <div className="text-[11px] text-zinc-500 mt-1 line-clamp-2">{p.descripcion}</div>}
                    {p.fecha_objetivo && (
                      <div className="text-[11px] text-amber-200 mt-1.5 flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> Objetivo: {new Date(p.fecha_objetivo).toLocaleDateString("es-ES")}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </PortalShell>
  );
}

function PortalEstadisticasView() {
  const [espacioSel, setEspacioSel] = useState(null);
  return (
    <>
      <EstadisticasView mode="portal" apiClient={api()} onEspacioClick={setEspacioSel} />
      {espacioSel && <PortalEspacioGaleria espacio={espacioSel} onClose={() => setEspacioSel(null)} />}
    </>
  );
}

function PortalPeticionDetalle({ pid, cliente, config, scope, onBack }) {
  const [pet, setPet] = useState(null);
  const [reportes, setReportes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api().get(`/portal/peticiones/${pid}`),
      config.ver_fotos_montaje ? api().get(`/portal/peticiones/${pid}/reportes`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
    ]).then(([p, r]) => {
      setPet(p.data); setReportes(r.data);
    }).catch((e) => toast.error(e?.response?.data?.detail || "Error cargando"))
      .finally(() => setLoading(false));
  }, [pid, config.ver_fotos_montaje]);

  if (loading) {
    return <PortalShell cliente={cliente}><div className="p-8 text-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline" /></div></PortalShell>;
  }
  if (!pet) return null;

  const borrarAdjunto = async (aid) => {
    if (!window.confirm("¿Eliminar este archivo?")) return;
    try {
      await api().delete(`/portal/peticiones/${pet.id}/adjuntos/${aid}`);
      setPet((prev) => ({ ...prev, adjuntos: (prev.adjuntos || []).filter((a) => a.id !== aid) }));
      toast.success("Archivo eliminado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const est = ESTADO_PETICION[pet.estado] || ESTADO_PETICION.borrador;
  return (
    <PortalShell cliente={cliente} onBack={onBack} title={pet.codigo}>
      <div className="p-4 sm:p-6 space-y-4">
        {/* Header petición */}
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="text-xs text-amber-400 font-mono">{pet.codigo}</span>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${est.color}`}>{est.label}</span>
            {pet.marca && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-violet-500/20 text-violet-300">{pet.marca}</span>}
          </div>
          <h1 className="text-lg sm:text-xl font-bold">{pet.nombre}</h1>
          {pet.descripcion && <p className="text-xs text-zinc-400 mt-2 whitespace-pre-wrap">{pet.descripcion}</p>}
          <div className="text-[11px] text-zinc-500 mt-3 flex flex-wrap gap-3">
            {pet.fecha_objetivo && <span><Calendar className="w-3 h-3 inline mr-0.5" /> Objetivo: {new Date(pet.fecha_objetivo).toLocaleDateString("es-ES")}</span>}
            {config.ver_economico && pet.presupuesto_total != null && (
              <span className="text-amber-300">Presupuesto: {pet.presupuesto_total.toLocaleString("es-ES", { style: "currency", currency: "EUR" })}</span>
            )}
          </div>
        </div>

        {/* Aprobaciones (Fase 4B) */}
        {(pet.puede_aprobar_presupuesto || pet.puede_aprobar_aaff) && (
          <AprobacionesPanel pet={pet} onChange={(updated) => setPet({ ...pet, ...updated })} />
        )}

        {/* KPIs de montaje — visibles si hay líneas */}
        {(pet.lineas || []).length > 0 && pet.kpis_montaje && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="portal-kpis-montaje">
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-zinc-100">{pet.kpis_montaje.total || 0}</div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">Total puntos</div>
            </div>
            <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-emerald-300">{pet.kpis_montaje.montado || 0}</div>
              <div className="text-[10px] uppercase tracking-wider text-emerald-400 mt-1">Montados</div>
            </div>
            <div className="bg-amber-500/5 border border-amber-500/30 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-amber-300">{pet.kpis_montaje.programada || 0}</div>
              <div className="text-[10px] uppercase tracking-wider text-amber-400 mt-1">Programados</div>
            </div>
            <div className="bg-red-500/5 border border-red-500/30 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-red-300">{pet.kpis_montaje.incidencia || 0}</div>
              <div className="text-[10px] uppercase tracking-wider text-red-400 mt-1">Incidencias</div>
            </div>
          </div>
        )}

        {/* Líneas (espacios) */}
        {(pet.lineas || []).length > 0 && (
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl overflow-hidden" data-testid="portal-lineas-list">
            <div className="bg-zinc-950/60 border-b border-zinc-800 px-3 py-2 text-[11px] uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
              <MapPin className="w-3 h-3" /> Espacios ({pet.lineas.length})
            </div>
            <div className="divide-y divide-zinc-800/60">
              {pet.lineas.map((l) => {
                const er = ESTADO_REAL[l.estado_real] || ESTADO_REAL.pendiente;
                return (
                <div key={l.id} className="p-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200">{TIPO_LINEA[l.tipo] || l.tipo}</span>
                      <span className="text-sm font-medium">{l.espacio?.nombre || "—"}</span>
                    </div>
                    <span data-testid={`linea-estado-${l.id}`}
                      className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${er.className}`}>
                      {er.label}
                    </span>
                  </div>
                  {l.espacio?.ciudad && (
                    <div className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-1">
                      <MapPin className="w-2.5 h-2.5" /> {l.espacio.direccion || ""}{l.espacio.ciudad ? `, ${l.espacio.ciudad}` : ""} {l.espacio.provincia && `(${l.espacio.provincia})`}
                    </div>
                  )}
                  {l.fecha_programada && (
                    <div className="text-[11px] text-amber-200 mt-1">📅 Programada: {new Date(l.fecha_programada).toLocaleDateString("es-ES")}</div>
                  )}
                  {/* Botón descargar fotos del espacio */}
                  {pet.id && (
                    <DescargarFotosBtn pid={pet.id} lineaId={l.id} />
                  )}
                  {/* Componentes con aprobación AAFF (solo si puede_aprobar_aaff) */}
                  {pet.puede_aprobar_aaff && (l.componentes || []).length > 0 && (
                    <div className="mt-2.5 space-y-1.5">
                      {l.componentes.map((c) => (
                        <ComponenteAprobacionRow key={c.id} comp={c}
                          onChange={(updated) => {
                            setPet({
                              ...pet,
                              lineas: pet.lineas.map(li => li.id !== l.id ? li : {
                                ...li,
                                componentes: li.componentes.map(cp => cp.id === c.id ? { ...cp, aprobacion_aaff: updated } : cp),
                              }),
                            });
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Adjuntos */}
        {config.ver_adjuntos && (pet.adjuntos || []).length > 0 && (
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-3" data-testid="portal-adjuntos">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5">
              <FileText className="w-3 h-3" /> Documentos ({pet.adjuntos.length})
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {pet.adjuntos.map((adj) => {
                const isImg = (adj.tipo || "").startsWith("image/");
                const propio = adj.subido_por === "portal_cliente";
                return (
                  <div key={adj.id} className="relative group">
                    <a href={adj.url} target="_blank" rel="noreferrer"
                      className="block bg-zinc-950 border border-zinc-800 hover:border-amber-500/40 rounded overflow-hidden transition-colors">
                      {isImg ? (
                        <img src={adj.url} alt={adj.nombre} className="w-full h-20 object-cover" />
                      ) : (
                        <div className="h-20 flex items-center justify-center text-zinc-400 bg-zinc-950"><FileText className="w-8 h-8" /></div>
                      )}
                      <div className="text-[10px] p-1 truncate text-zinc-300">{adj.nombre}</div>
                    </a>
                    {propio && (
                      <button onClick={() => borrarAdjunto(adj.id)}
                        data-testid={`borrar-adjunto-${adj.id}`}
                        title="Eliminar (subido por ti)"
                        className="absolute top-1 right-1 p-1 rounded bg-red-500/80 hover:bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Reportes de montaje aprobados */}
        {config.ver_fotos_montaje && reportes.length > 0 && (
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-3" data-testid="portal-reportes">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-3 flex items-center gap-1.5">
              <ImgIcon className="w-3 h-3" /> Reportes de montaje ({reportes.length})
            </div>
            <div className="space-y-3">
              {reportes.map((r, i) => (
                <ReporteCard key={i} reporte={r} ruta_id={r.ruta_id} parada_idx={r.parada_idx} config={config} />
              ))}
            </div>
          </div>
        )}

        {config.ver_fotos_montaje && reportes.length === 0 && (
          <div className="text-center text-xs text-zinc-500 py-6 italic">
            Aún no hay reportes de montaje aprobados publicados.
          </div>
        )}
      </div>
    </PortalShell>
  );
}

function ReporteCard({ reporte, config }) {
  const estado = reporte.estado_cierre;
  const tipo = reporte.tipo_incidencia;
  return (
    <div className="bg-zinc-950/60 border border-zinc-800 rounded-lg p-2.5" data-testid={`portal-reporte-${reporte.ruta_codigo}-${reporte.parada_idx}`}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div className="text-sm font-semibold">{reporte.espacio_nombre || "—"}</div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
            estado === "montado" ? "bg-emerald-500/20 text-emerald-200" :
            estado === "semi_montado" ? "bg-amber-500/20 text-amber-200" :
            estado === "cancelado" ? "bg-zinc-700 text-zinc-300" :
            "bg-red-500/20 text-red-200"
          }`}>{(estado || "").replace("_", " ")}</span>
          {tipo && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-200">{tipo}</span>}
        </div>
      </div>
      {reporte.fecha && <FechaMontajePortal reporte={reporte} />}
      {reporte.montadores && reporte.montadores.length > 0 && (
        <div className="flex items-center gap-2 mb-2 px-2 py-1.5 rounded-md bg-gradient-to-r from-amber-500/10 to-transparent border border-amber-500/20"
             data-testid={`portal-reporte-${reporte.ruta_codigo}-${reporte.parada_idx}-montadores`}>
          <span className="text-[9px] uppercase tracking-wider text-amber-300 font-semibold">Montaje realizado por</span>
          <div className="flex items-center gap-1.5 flex-wrap">
            {reporte.montadores.map((m, mi) => (
              <div key={mi} className="flex items-center gap-1.5 bg-zinc-900/60 rounded-full pl-0.5 pr-2 py-0.5">
                {m.foto_url ? (
                  <img src={m.foto_url} alt={m.nombre}
                    className="w-6 h-6 rounded-full object-cover border border-amber-500/40"
                    title={m.nombre} />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-[10px] font-bold text-amber-300"
                    title={m.nombre}>
                    {(m.nombre || "?").slice(0, 1).toUpperCase()}
                  </div>
                )}
                <span className="text-[11px] text-zinc-100 font-medium">{m.nombre}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {reporte.incidencia_descripcion && (
        <div className="text-[11px] text-zinc-300 bg-zinc-900 border border-zinc-800 rounded p-2 mb-2 whitespace-pre-wrap">
          💬 {reporte.incidencia_descripcion}
        </div>
      )}
      {reporte.fotos && reporte.fotos.length > 0 && (
        <PhotoGrid fotos={reporte.fotos} nameBase={nombreFotoBase(reporte.espacio_nombre, reporte.marca)} />
      )}
    </div>
  );
}

function FechaMontajePortal({ reporte }) {
  const [fecha, setFecha] = useState(reporte.fecha);
  const [editada, setEditada] = useState(!!reporte.fecha_editada);
  const [editando, setEditando] = useState(false);
  const [val, setVal] = useState((reporte.fecha || "").slice(0, 10));
  const [saving, setSaving] = useState(false);

  const guardar = async () => {
    if (!val) return;
    setSaving(true);
    try {
      const { data } = await api().patch("/portal/montajes/fecha", {
        ruta_id: reporte.ruta_id, parada_idx: reporte.parada_idx, fecha: val,
      });
      setFecha(data.fecha); setEditada(true); setEditando(false);
      toast.success("Fecha de montaje actualizada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    } finally { setSaving(false); }
  };

  return (
    <div className="text-[10px] text-zinc-500 mb-2 flex items-center gap-1.5 flex-wrap" data-testid={`portal-fecha-montaje-${reporte.ruta_codigo}-${reporte.parada_idx}`}>
      {editando ? (
        <>
          <input type="date" value={val} onChange={(e) => setVal(e.target.value)}
            data-testid="portal-fecha-montaje-input"
            className="bg-zinc-900 border border-zinc-700 rounded px-1.5 py-0.5 text-[11px] text-zinc-200" />
          <button onClick={guardar} disabled={saving}
            data-testid="portal-fecha-montaje-guardar"
            className="text-emerald-300 hover:text-emerald-200 font-semibold disabled:opacity-50">
            {saving ? "…" : "Guardar"}
          </button>
          <button onClick={() => setEditando(false)} className="text-zinc-500 hover:text-zinc-300">Cancelar</button>
        </>
      ) : (
        <>
          <span>📅 {new Date(fecha).toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" })}</span>
          {editada && <span className="text-amber-400/70 italic">(corregida)</span>}
          {reporte.ruta_id != null && reporte.parada_idx != null && (
            <button onClick={() => { setVal((fecha || "").slice(0, 10)); setEditando(true); }}
              data-testid="portal-fecha-montaje-editar"
              title="Corregir fecha del montaje"
              className="text-zinc-500 hover:text-amber-300 transition-colors">
              <Pencil className="w-3 h-3" />
            </button>
          )}
        </>
      )}
    </div>
  );
}


function PhotoGrid({ fotos, nameBase }) {
  const [idx, setIdx] = useState(null); // null = cerrado

  return (
    <>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-1">
        {fotos.map((f, i) => (
          <button key={i} type="button" onClick={() => setIdx(i)}
            data-testid={`portal-foto-${i}`}
            className="block aspect-square overflow-hidden rounded border border-zinc-800 hover:border-amber-500/40 transition group relative">
            <img src={f.url} alt={f.categoria || "foto"} className="w-full h-full object-cover group-hover:scale-105 transition duration-300" loading="lazy" />
            {f.categoria && <span className="absolute bottom-1 left-1 right-1 text-[9px] bg-black/60 text-white px-1.5 py-0.5 rounded truncate">{f.categoria}</span>}
          </button>
        ))}
      </div>
      <PhotoLightbox
        fotos={fotos}
        openIdx={idx}
        onClose={() => setIdx(null)}
        nameForIdx={nameBase ? (i) => `${nameBase}_${String(i + 1).padStart(2, "0")}` : undefined}
      />
    </>
  );
}


function AprobacionEstado({ aprobacion }) {
  if (!aprobacion) return <span className="text-[10px] uppercase tracking-wider text-zinc-500">Pendiente</span>;
  const ok = aprobacion.estado === "aprobada";
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded inline-flex items-center gap-1 ${
      ok ? "bg-emerald-500/20 text-emerald-200" : "bg-red-500/20 text-red-200"
    }`}>
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
      {ok ? "Aprobada" : "Rechazada"}
    </span>
  );
}

function AprobacionDetalle({ aprobacion }) {
  if (!aprobacion) return null;
  return (
    <div className={`mt-2 p-2 rounded text-[11px] ${
      aprobacion.estado === "aprobada" ? "bg-emerald-500/10 text-emerald-100" : "bg-red-500/10 text-red-100"
    }`}>
      <div className="flex items-center justify-between flex-wrap gap-1">
        <span className="font-medium">{aprobacion.por}</span>
        <span className="opacity-70">{new Date(aprobacion.fecha).toLocaleString("es-ES")}</span>
      </div>
      {aprobacion.comentario && (
        <div className="mt-1 flex gap-1"><MessageSquare className="w-3 h-3 mt-0.5 shrink-0" /> {aprobacion.comentario}</div>
      )}
    </div>
  );
}

function ConfirmDialog({ open, onClose, onConfirm, accion, item, requireComment }) {
  const [comentario, setComentario] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (requireComment && !comentario.trim()) {
      toast.error("El comentario es obligatorio");
      return;
    }
    setBusy(true);
    try {
      await onConfirm(comentario.trim());
      setComentario("");
      onClose();
    } finally {
      setBusy(false);
    }
  };
  const isRechazo = accion === "rechazar";
  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isRechazo ? <XCircle className="w-5 h-5 text-red-400" /> : <CheckCircle2 className="w-5 h-5 text-emerald-400" />}
            {isRechazo ? "Rechazar" : "Aprobar"} — {item}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit}>
          <div className="text-xs text-zinc-400 mb-2">
            {isRechazo
              ? "Describe el motivo del rechazo. El equipo lo revisará y te enviará una nueva versión."
              : "Confirma que estás de acuerdo. Puedes añadir un comentario opcional."}
          </div>
          <Textarea value={comentario} onChange={(e) => setComentario(e.target.value)}
            data-testid="aprobacion-comentario"
            placeholder={isRechazo ? "Motivo obligatorio…" : "Comentario (opcional)"}
            className="bg-zinc-900 border-zinc-800 min-h-[100px]"
            required={isRechazo} />
          <DialogFooter className="mt-3">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}
              className="bg-transparent border-zinc-700 text-zinc-300">Cancelar</Button>
            <Button type="submit" disabled={busy}
              data-testid="aprobacion-confirmar"
              className={`font-semibold ${isRechazo ? "bg-red-500 hover:bg-red-600 text-white" : "bg-emerald-500 hover:bg-emerald-600 text-zinc-950"}`}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (isRechazo ? "Rechazar" : "Aprobar")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AprobacionesPanel({ pet, onChange }) {
  const [dialog, setDialog] = useState(null); // { tipo: presupuesto|aaff, accion: aprobar|rechazar }

  const handle = async (comentario) => {
    if (!dialog) return;
    const slug = dialog.tipo === "presupuesto" ? "presupuesto" : "aaff";
    const accion = dialog.accion === "aprobar" ? "aprobar" : "rechazar";
    try {
      const { data } = await api().post(`/portal/peticiones/${pet.id}/${accion}-${slug}`, { comentario });
      toast.success(dialog.accion === "aprobar" ? "✅ Aprobación registrada" : "Rechazo registrado");
      const field = dialog.tipo === "presupuesto" ? "aprobacion_presupuesto" : "aprobacion_aaff_global";
      onChange({ [field]: data.aprobacion });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error procesando");
      throw e;
    }
  };

  const showPresup = pet.puede_aprobar_presupuesto;
  const showAaff = pet.puede_aprobar_aaff;
  const ap = pet.aprobacion_presupuesto;
  const aa = pet.aprobacion_aaff_global;

  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl overflow-hidden" data-testid="portal-aprobaciones-panel">
      <div className="bg-zinc-950/60 border-b border-zinc-800 px-3 py-2 text-[11px] uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
        <ThumbsUp className="w-3 h-3" /> Tu aprobación
      </div>
      <div className="divide-y divide-zinc-800/60">
        {showPresup && (
          <div className="p-3">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Presupuesto</span>
                <AprobacionEstado aprobacion={ap} />
              </div>
              {!ap && (
                <div className="flex gap-1.5">
                  <Button size="sm" onClick={() => setDialog({ tipo: "presupuesto", accion: "aprobar" })}
                    data-testid="aprobar-presupuesto-btn"
                    className="bg-emerald-500 hover:bg-emerald-600 text-zinc-950 h-7 text-xs font-semibold">
                    <ThumbsUp className="w-3 h-3 mr-1" /> Aprobar
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setDialog({ tipo: "presupuesto", accion: "rechazar" })}
                    data-testid="rechazar-presupuesto-btn"
                    className="bg-transparent border-red-500/40 text-red-300 hover:bg-red-500/10 h-7 text-xs">
                    <ThumbsDown className="w-3 h-3 mr-1" /> Rechazar
                  </Button>
                </div>
              )}
            </div>
            <AprobacionDetalle aprobacion={ap} />
          </div>
        )}
        {showAaff && (
          <div className="p-3">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">AAFF (global)</span>
                <AprobacionEstado aprobacion={aa} />
              </div>
              {!aa && (
                <div className="flex gap-1.5">
                  <Button size="sm" onClick={() => setDialog({ tipo: "aaff", accion: "aprobar" })}
                    data-testid="aprobar-aaff-btn"
                    className="bg-emerald-500 hover:bg-emerald-600 text-zinc-950 h-7 text-xs font-semibold">
                    <ThumbsUp className="w-3 h-3 mr-1" /> Aprobar
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setDialog({ tipo: "aaff", accion: "rechazar" })}
                    data-testid="rechazar-aaff-btn"
                    className="bg-transparent border-red-500/40 text-red-300 hover:bg-red-500/10 h-7 text-xs">
                    <ThumbsDown className="w-3 h-3 mr-1" /> Rechazar
                  </Button>
                </div>
              )}
            </div>
            <AprobacionDetalle aprobacion={aa} />
            {showAaff && (
              <div className="text-[10px] text-zinc-500 mt-1.5 italic">
                💡 También puedes aprobar/rechazar componente a componente en la sección "Espacios".
              </div>
            )}
          </div>
        )}
      </div>
      <ConfirmDialog
        open={!!dialog}
        onClose={() => setDialog(null)}
        onConfirm={handle}
        accion={dialog?.accion}
        item={dialog?.tipo === "presupuesto" ? "Presupuesto" : "AAFF (global)"}
        requireComment={dialog?.accion === "rechazar"}
      />
    </div>
  );
}

function ComponenteAprobacionRow({ comp, onChange }) {
  const [dialog, setDialog] = useState(null); // { accion: aprobar|rechazar }
  const handle = async (comentario) => {
    if (!dialog) return;
    try {
      const { data } = await api().post(`/portal/componentes/${comp.id}/${dialog.accion}`, { comentario });
      toast.success(dialog.accion === "aprobar" ? "✅ Componente aprobado" : "Rechazo registrado");
      onChange(data.aprobacion);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
      throw e;
    }
  };
  const a = comp.aprobacion_aaff;
  return (
    <div className="flex items-start gap-2 bg-zinc-950/60 border border-zinc-800 rounded p-2"
      data-testid={`portal-componente-aprobacion-${comp.id}`}>
      {comp.imagen_url ? (
        <img src={comp.imagen_url} alt={comp.nombre} className="w-12 h-12 object-cover rounded border border-zinc-800" />
      ) : (
        <div className="w-12 h-12 rounded bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-600">
          <Package className="w-5 h-5" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-xs font-medium truncate">{comp.nombre || "—"}</span>
          <AprobacionEstado aprobacion={a} />
        </div>
        <div className="text-[10px] text-zinc-500">
          {comp.ancho_cm && comp.alto_cm ? `${Math.round(comp.ancho_cm)}×${Math.round(comp.alto_cm)}cm` : ""}
          {comp.descripcion && ` · ${comp.descripcion.slice(0, 60)}`}
        </div>
        {a && a.comentario && (
          <div className={`text-[10px] mt-1 ${a.estado === "rechazada" ? "text-red-300" : "text-emerald-300"}`}>
            💬 {a.comentario}
          </div>
        )}
        {!a && (
          <div className="flex gap-1 mt-1.5">
            <Button size="sm" onClick={() => setDialog({ accion: "aprobar" })}
              data-testid={`aprobar-comp-${comp.id}`}
              className="bg-emerald-500 hover:bg-emerald-600 text-zinc-950 h-6 text-[10px] px-2">
              <ThumbsUp className="w-2.5 h-2.5 mr-0.5" /> OK
            </Button>
            <Button size="sm" variant="outline" onClick={() => setDialog({ accion: "rechazar" })}
              data-testid={`rechazar-comp-${comp.id}`}
              className="bg-transparent border-red-500/40 text-red-300 hover:bg-red-500/10 h-6 text-[10px] px-2">
              <ThumbsDown className="w-2.5 h-2.5 mr-0.5" /> Cambios
            </Button>
          </div>
        )}
      </div>
      <ConfirmDialog
        open={!!dialog}
        onClose={() => setDialog(null)}
        onConfirm={handle}
        accion={dialog?.accion}
        item={comp.nombre || "Componente"}
        requireComment={dialog?.accion === "rechazar"}
      />
    </div>
  );
}

function DescargarFotosBtn({ pid, lineaId }) {
  const [busy, setBusy] = useState(false);
  const handleDownload = async () => {
    setBusy(true);
    try {
      const token = getPortalToken();
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/portal/peticiones/${pid}/lineas/${lineaId}/fotos.zip`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        toast.info(err.detail || "Aún no hay fotos aprobadas para descargar");
        return;
      }
      const blob = await resp.blob();
      const cd = resp.headers.get("content-disposition") || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      const filename = m ? m[1] : `fotos_${lineaId}.zip`;
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success("📦 ZIP descargado");
    } catch (e) {
      toast.error("Error al descargar");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button onClick={handleDownload} disabled={busy}
      data-testid={`descargar-fotos-${lineaId}`}
      className="mt-2 text-[11px] text-amber-300 hover:text-amber-200 flex items-center gap-1 disabled:opacity-50">
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
      Descargar todas las fotos (ZIP)
    </button>
  );
}

